import { useState, useEffect, useRef } from 'react';
import {
  UtensilsCrossed, Clock, Star, Truck, Flame, Users, Wine, ChefHat,
  Calendar, Zap, Percent, Heart, MapPin, Phone as PhoneIcon,
  MessageCircle, X, Send, Loader2, Coffee, Leaf, Pizza,
  Instagram, ChevronDown, Quote, Sparkles, Award, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  utensils: UtensilsCrossed, clock: Clock, star: Star, truck: Truck,
  flame: Flame, users: Users, wine: Wine, 'chef-hat': ChefHat,
  calendar: Calendar, zap: Zap, percent: Percent, heart: Heart,
  coffee: Coffee, leaf: Leaf, pizza: Pizza, sparkles: Sparkles, award: Award,
};

const isDarkBg = (hex) => {
  if (!hex || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
};

const hexToRgba = (hex, a = 1) => {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${a})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

/* ============ EMBEDDED MINI CHAT ============ */
function MiniChat({ branchId, primaryColor }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: 'assistant', content: 'يا هلا وغلا! كيف أقدر أساعدك؟' }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sid] = useState(() => `preview-${Date.now()}`);

  const send = async () => {
    if (!input.trim() || loading) return;
    const m = input.trim(); setInput('');
    setMsgs(p => [...p, { role: 'user', content: m }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat`, { branch_id: branchId || 'default', session_id: sid, message: m });
      setMsgs(p => [...p, { role: 'assistant', content: r.data.response }]);
    } catch { setMsgs(p => [...p, { role: 'assistant', content: 'عذراً، حاول مرة ثانية' }]); }
    finally { setLoading(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="preview-chat-toggle"
      className="zq-chat-fab" style={{ backgroundColor: primaryColor || '#D97706' }}>
      <MessageCircle style={{ width: 22, height: 22 }} />
    </button>
  );

  return (
    <div data-testid="preview-chat-panel" className="zq-chat-panel">
      <div className="zq-chat-header" style={{ background: `linear-gradient(135deg, ${primaryColor || '#D97706'}, ${primaryColor || '#D97706'}dd)` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MessageCircle style={{ width: 16, height: 16, color: '#fff' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#fff', fontFamily: 'Tajawal, sans-serif' }}>ZouqBot</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>متصل الآن</div>
          </div>
        </div>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <X style={{ width: 16, height: 16, color: 'rgba(255,255,255,0.8)' }} />
        </button>
      </div>
      <div dir="rtl" style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-start' : 'flex-end' }}>
            <div style={{ maxWidth: '80%', padding: '8px 14px', borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', backgroundColor: m.role === 'user' ? (primaryColor || '#D97706') : '#f3f4f6', color: m.role === 'user' ? '#fff' : '#111', fontSize: 12, lineHeight: 1.7, fontFamily: 'Tajawal, sans-serif' }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && <div style={{ display: 'flex', justifyContent: 'flex-end' }}><div style={{ padding: '8px 14px', borderRadius: 16, backgroundColor: '#f3f4f6' }}><Loader2 className="zq-spin" style={{ width: 14, height: 14 }} /></div></div>}
      </div>
      <div dir="rtl" style={{ padding: 10, borderTop: '1px solid #eee', display: 'flex', gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send(); }} placeholder="اكتب رسالتك..." disabled={loading} data-testid="preview-chat-input"
          style={{ flex: 1, height: 36, borderRadius: 20, border: '1px solid #ddd', padding: '0 14px', fontSize: 12, fontFamily: 'Tajawal, sans-serif', outline: 'none' }} />
        <button onClick={send} disabled={!input.trim() || loading} data-testid="preview-chat-send"
          style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: primaryColor || '#D97706', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: (!input.trim() || loading) ? 0.5 : 1 }}>
          <Send style={{ width: 14, height: 14 }} />
        </button>
      </div>
    </div>
  );
}

/* ============ CSS ENGINE V2 ============ */
const getCSS = (c, f, rad, dark) => `
@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700;800;900&family=Cairo:wght@400;600;700;800;900&family=Amiri:wght@400;700&family=Almarai:wght@300;400;700;800&family=Playfair+Display:wght@400;700;900&family=Noto+Kufi+Arabic:wght@400;700;800;900&family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap');

.zq-root {
  --zq-primary: ${c.primary || '#D97706'};
  --zq-secondary: ${c.secondary || c.primary || '#D97706'};
  --zq-accent: ${c.accent || '#10B981'};
  --zq-bg: ${c.bg || '#fff'};
  --zq-text: ${c.text || '#1C1917'};
  --zq-text-light: ${c.text_light || '#78716C'};
  --zq-card: ${c.card_bg || '#fff'};
  --zq-section: ${c.bg_section || '#FAFAF9'};
  --zq-rad: ${rad || '16px'};
  --zq-glass: ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
  --zq-glass-border: ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  --zq-shadow: ${dark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.06)'};
  --zq-shadow-lg: ${dark ? '0 24px 64px rgba(0,0,0,0.5)' : '0 24px 64px rgba(0,0,0,0.08)'};
  scroll-behavior: smooth;
  font-family: '${f.body || 'Tajawal'}', sans-serif;
}

@keyframes zqFadeUp { from { opacity:0; transform:translateY(50px); } to { opacity:1; transform:translateY(0); } }
@keyframes zqFadeIn { from { opacity:0; } to { opacity:1; } }
@keyframes zqScaleIn { from { opacity:0; transform:scale(0.9); } to { opacity:1; transform:scale(1); } }
@keyframes zqSlideR { from { opacity:0; transform:translateX(60px); } to { opacity:1; transform:translateX(0); } }
@keyframes zqSlideL { from { opacity:0; transform:translateX(-60px); } to { opacity:1; transform:translateX(0); } }
@keyframes zqFloat { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-18px); } }
@keyframes zqPulse { 0%,100% { opacity:0.2; } 50% { opacity:0.5; } }
@keyframes zqShine { 0% { left:-100%; } 100% { left:200%; } }
@keyframes zqGrow { from { width:0; } to { width:100%; } }

.zq-spin { animation: zqSpin 1s linear infinite; }
@keyframes zqSpin { to { transform:rotate(360deg); } }

.zq-section { opacity:0; transform:translateY(40px); transition: opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1); }
.zq-section.zq-vis { opacity:1; transform:translateY(0); }

.zq-hero { opacity:1 !important; transform:none !important; }
.zq-hero-content > * { opacity:0; animation: zqFadeUp 1s cubic-bezier(0.16,1,0.3,1) forwards; }
.zq-hero-content > *:nth-child(1) { animation-delay:0.2s; }
.zq-hero-content > *:nth-child(2) { animation-delay:0.4s; }
.zq-hero-content > *:nth-child(3) { animation-delay:0.6s; }
.zq-hero-content > *:nth-child(4) { animation-delay:0.8s; }
.zq-hero-content > *:nth-child(5) { animation-delay:1s; }
.zq-hero-img { opacity:0; animation: zqSlideR 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s forwards; }

.zq-stagger > * { opacity:0; transform:translateY(30px); transition: opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1); }
.zq-vis .zq-stagger > * { opacity:1; transform:none; }
.zq-vis .zq-stagger > *:nth-child(1) { transition-delay:0s; }
.zq-vis .zq-stagger > *:nth-child(2) { transition-delay:0.1s; }
.zq-vis .zq-stagger > *:nth-child(3) { transition-delay:0.2s; }
.zq-vis .zq-stagger > *:nth-child(4) { transition-delay:0.3s; }
.zq-vis .zq-stagger > *:nth-child(5) { transition-delay:0.4s; }
.zq-vis .zq-stagger > *:nth-child(6) { transition-delay:0.5s; }

.zq-card {
  background: var(--zq-card);
  border: 1px solid var(--zq-glass-border);
  border-radius: var(--zq-rad);
  transition: transform 0.5s cubic-bezier(0.16,1,0.3,1), box-shadow 0.5s;
  overflow: hidden;
}
.zq-card:hover {
  transform: translateY(-10px);
  box-shadow: var(--zq-shadow-lg);
}

.zq-glass {
  background: var(--zq-glass);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--zq-glass-border);
  border-radius: var(--zq-rad);
  transition: transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.4s;
}
.zq-glass:hover { transform: translateY(-6px); box-shadow: var(--zq-shadow); }

.zq-img-wrap { overflow:hidden; border-radius: var(--zq-rad) var(--zq-rad) 0 0; }
.zq-img-wrap img { transition: transform 0.8s cubic-bezier(0.16,1,0.3,1); width:100%; height:100%; object-fit:cover; }
.zq-card:hover .zq-img-wrap img { transform:scale(1.08); }

.zq-cta {
  position:relative; overflow:hidden; cursor:pointer; border:none; font-weight:700;
  transition: transform 0.3s, box-shadow 0.3s;
}
.zq-cta:hover { transform:translateY(-3px); box-shadow: 0 16px 40px ${hexToRgba(c.primary, 0.35)}; }
.zq-cta::after {
  content:''; position:absolute; top:0; left:-100%; width:60%; height:100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
}
.zq-cta:hover::after { animation: zqShine 0.7s ease forwards; }

.zq-nav-link { position:relative; cursor:pointer; transition:color 0.3s; }
.zq-nav-link::after { content:''; position:absolute; bottom:-4px; right:0; width:0; height:2px; background:var(--zq-primary); transition:width 0.3s; border-radius:1px; }
.zq-nav-link:hover::after { width:100%; }
.zq-nav-link:hover { color:var(--zq-primary) !important; }

.zq-float { animation: zqFloat 7s ease-in-out infinite; }
.zq-float-2 { animation: zqFloat 8s ease-in-out 1.5s infinite; }
.zq-pulse { animation: zqPulse 5s ease-in-out infinite; }

.zq-stars { color: #FBBF24; display:flex; gap:2px; }

.zq-root::-webkit-scrollbar { width:5px; }
.zq-root::-webkit-scrollbar-track { background:transparent; }
.zq-root::-webkit-scrollbar-thumb { background:${hexToRgba(c.primary, 0.3)}; border-radius:3px; }

.zq-chat-fab { position:absolute; bottom:20px; left:20px; z-index:50; width:54px; height:54px; border-radius:50%; color:#fff; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; box-shadow:0 6px 28px rgba(0,0,0,0.25); transition:transform 0.3s, box-shadow 0.3s; }
.zq-chat-fab:hover { transform:scale(1.1) translateY(-2px); }
.zq-chat-panel { position:absolute; bottom:20px; left:20px; z-index:50; width:320px; height:420px; background:#fff; border-radius:20px; box-shadow:0 16px 50px rgba(0,0,0,0.2); display:flex; flex-direction:column; overflow:hidden; border:1px solid #e5e5e5; }
.zq-chat-header { padding:14px 16px; display:flex; align-items:center; justify-content:space-between; }

.zq-price { background:${hexToRgba(c.primary, 0.1)}; color:var(--zq-primary); padding:5px 14px; border-radius:20px; font-weight:800; white-space:nowrap; }

.zq-divider { width:50px; height:3px; background:var(--zq-primary); border-radius:2px; }

.zq-grain::after { content:''; position:absolute; inset:0; opacity:${dark ? 0.04 : 0.025}; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E"); pointer-events:none; z-index:1; }
`;

/* ============ MAIN PREVIEW ============ */
export default function SitePreview({ config, menuItems = [], className = '' }) {
  const rootRef = useRef(null);
  const [vis, setVis] = useState(new Set(['hero']));

  useEffect(() => {
    const container = rootRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(entry => {
        if (entry.isIntersecting) {
          const key = entry.target.getAttribute('data-section');
          if (key) setVis(prev => new Set([...prev, key]));
        }
      }),
      { threshold: 0.08, root: container }
    );
    const secs = container.querySelectorAll('[data-section]');
    secs.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, [config?.section_order]);

  if (!config) return null;

  const c = config.colors || {};
  const f = config.fonts || {};
  const hero = config.hero || {};
  const about = config.about || {};
  const features = config.features || [];
  const contact = config.contact || {};
  const highlights = config.menu_highlights || [];
  const gallery = config.gallery_images || [];
  const stats = config.stats || [];
  const testimonials = config.testimonials || [];
  const social = config.social_links || {};
  const ctaData = config.cta || {};
  const rName = config.restaurant_name || 'مطعمي';
  const tagline = config.tagline || '';
  const rad = config.border_radius || '16px';
  const showChat = config.show_chat_widget !== false;
  const sectionOrder = config.section_order || ['hero', 'stats', 'features', 'menu', 'about', 'testimonials', 'gallery', 'contact'];
  const dark = isDarkBg(c.bg);
  const hf = f.heading || 'Tajawal';
  const bf = f.body || 'Tajawal';

  // Resolve hero variant (backward compat)
  const heroVariant = config.hero_variant || (config.hero_style === 'full' ? 'immersive' : config.hero_style === 'centered' ? 'minimal' : 'split');

  const displayMenu = highlights.length > 0 ? highlights : menuItems.slice(0, 6).map(m => ({
    name: m.name_ar || m.name, price: m.price, description: m.description_ar || m.description, category: m.category, image_url: m.image_url
  }));

  const sc = (key) => `zq-section ${vis.has(key) ? 'zq-vis' : ''}`;

  /* ════════ HERO: CINEMATIC ════════ */
  const renderHeroCinematic = () => (
    <section className="zq-hero zq-grain" data-section="hero" style={{ position: 'relative', minHeight: 620, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img src={hero.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200&q=80'} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 50%, ${hexToRgba(c.bg, 0.95)} 100%)` }} />
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.3) 100%)` }} />

      <div dir="rtl" className="zq-hero-content" style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '0 48px', maxWidth: 700 }}>
        {tagline && <div style={{ fontFamily: `'${bf}'`, fontSize: 13, color: c.primary, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 20 }}>{tagline}</div>}
        <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 28px' }} />
        <h1 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 56, lineHeight: 1.2, color: '#FFFFFF', marginBottom: 24, textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
          {hero.title || 'أهلاً بكم'}
        </h1>
        <p style={{ fontFamily: `'${bf}'`, fontSize: 17, lineHeight: 2, color: 'rgba(255,255,255,0.8)', marginBottom: 40, maxWidth: 520, margin: '0 auto 40px' }}>
          {hero.subtitle}
        </p>
        <button className="zq-cta" style={{ backgroundColor: c.primary, color: '#fff', borderRadius: rad, padding: '18px 52px', fontSize: 17, fontFamily: `'${bf}'`, boxShadow: `0 12px 40px ${hexToRgba(c.primary, 0.5)}` }}>
          {hero.cta_text || 'اطلب الآن'}
        </button>
      </div>

      <div className="zq-float" style={{ position: 'absolute', top: 60, left: 80, width: 100, height: 100, borderRadius: '50%', border: `1px solid ${hexToRgba(c.primary, 0.15)}` }} />
      <div className="zq-float-2" style={{ position: 'absolute', bottom: 100, right: 60, width: 60, height: 60, borderRadius: '50%', background: hexToRgba(c.primary, 0.08) }} />

      <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 3, animation: 'zqFloat 2s ease-in-out infinite', opacity: 0.4 }}>
        <ChevronDown style={{ width: 22, height: 22, color: 'rgba(255,255,255,0.6)' }} />
      </div>
    </section>
  );

  /* ════════ HERO: EDITORIAL ════════ */
  const renderHeroEditorial = () => (
    <section className="zq-hero zq-grain" data-section="hero" style={{ display: 'grid', gridTemplateColumns: '50% 50%', minHeight: 580, position: 'relative', overflow: 'hidden', backgroundColor: c.bg }}>
      <div dir="rtl" className="zq-hero-content" style={{ padding: '80px 60px', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
        {tagline && <div style={{ fontFamily: `'${bf}'`, fontSize: 12, color: c.primary, fontWeight: 700, letterSpacing: '0.15em', marginBottom: 20 }}>{tagline}</div>}
        <div className="zq-divider" style={{ marginBottom: 28 }} />
        <h1 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 48, lineHeight: 1.25, color: c.text, marginBottom: 24 }}>
          {hero.title || 'أهلاً بكم'}
        </h1>
        <p style={{ fontFamily: `'${bf}'`, fontSize: 16, lineHeight: 2.1, color: c.text_light, marginBottom: 40, maxWidth: 440 }}>
          {hero.subtitle}
        </p>
        <button className="zq-cta" style={{ backgroundColor: c.primary, color: '#fff', borderRadius: rad, padding: '16px 44px', fontSize: 16, fontFamily: `'${bf}'`, width: 'fit-content', boxShadow: `0 10px 35px ${hexToRgba(c.primary, 0.3)}` }}>
          {hero.cta_text || 'اطلب الآن'}
        </button>
      </div>

      <div className="zq-hero-img" style={{ position: 'relative', overflow: 'hidden' }}>
        <img src={hero.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80'} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to left, transparent 50%, ${c.bg})` }} />
        <div style={{ position: 'absolute', bottom: 40, left: 40, width: 80, height: 80, border: `3px solid ${hexToRgba(c.primary, 0.4)}`, borderRadius: rad }} />
        <div style={{ position: 'absolute', top: 40, left: 40, width: 6, height: 60, background: c.primary, borderRadius: 3, opacity: 0.4 }} />
      </div>

      <div className="zq-float" style={{ position: 'absolute', top: 50, left: '48%', width: 10, height: 10, borderRadius: '50%', background: c.primary, opacity: 0.3 }} />
    </section>
  );

  /* ════════ HERO: MINIMAL ════════ */
  const renderHeroMinimal = () => (
    <section className="zq-hero zq-grain" data-section="hero" style={{ position: 'relative', textAlign: 'center', padding: '120px 48px 100px', background: `linear-gradient(160deg, ${c.bg}, ${c.bg_section})`, overflow: 'hidden' }}>
      <div className="zq-float" style={{ position: 'absolute', top: 60, right: 100, width: 140, height: 140, borderRadius: '50%', border: `2px solid ${hexToRgba(c.primary, 0.08)}` }} />
      <div className="zq-float-2" style={{ position: 'absolute', bottom: 80, left: 80, width: 100, height: 100, borderRadius: '50%', background: hexToRgba(c.primary, 0.04) }} />
      <div className="zq-pulse" style={{ position: 'absolute', top: 150, left: '25%', width: 8, height: 8, borderRadius: '50%', background: c.primary }} />
      <div className="zq-pulse" style={{ position: 'absolute', bottom: 120, right: '20%', width: 6, height: 6, borderRadius: '50%', background: hexToRgba(c.primary, 0.5) }} />

      <div dir="rtl" className="zq-hero-content" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ fontSize: 12, color: c.primary, fontWeight: 700, letterSpacing: '0.2em', fontFamily: `'${bf}'`, marginBottom: 28 }}>{tagline || rName}</div>
        <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 32px' }} />
        <h1 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 52, lineHeight: 1.2, color: c.text, marginBottom: 24, maxWidth: 600, margin: '0 auto 24px' }}>
          {hero.title || 'أهلاً بكم'}
        </h1>
        <p style={{ fontFamily: `'${bf}'`, fontSize: 16, lineHeight: 2.1, color: c.text_light, marginBottom: 44, maxWidth: 500, margin: '0 auto 44px' }}>
          {hero.subtitle}
        </p>
        <button className="zq-cta" style={{ backgroundColor: c.primary, color: dark ? c.bg : '#fff', borderRadius: rad, padding: '18px 52px', fontSize: 16, fontFamily: `'${bf}'`, boxShadow: `0 10px 35px ${hexToRgba(c.primary, 0.3)}` }}>
          {hero.cta_text || 'اطلب الآن'}
        </button>
      </div>
    </section>
  );

  /* ════════ HERO: IMMERSIVE ════════ */
  const renderHeroImmersive = () => (
    <section className="zq-hero zq-grain" data-section="hero" style={{ position: 'relative', height: 600, overflow: 'hidden' }}>
      <img src={hero.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200&q=80'} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${c.bg} 0%, ${hexToRgba(c.bg, 0.85)} 30%, ${hexToRgba(c.bg, 0.2)} 70%, rgba(0,0,0,0.1) 100%)` }} />

      <div dir="rtl" className="zq-hero-content" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '60px 56px 72px', textAlign: 'center', zIndex: 2 }}>
        {tagline && <div style={{ fontSize: 13, color: c.primary, fontWeight: 700, letterSpacing: '0.12em', marginBottom: 20, fontFamily: `'${bf}'` }}>{tagline}</div>}
        <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 24px' }} />
        <h1 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 52, lineHeight: 1.2, color: c.text, marginBottom: 20, textShadow: dark ? '0 4px 30px rgba(0,0,0,0.5)' : 'none' }}>
          {hero.title || 'أهلاً بكم'}
        </h1>
        <p style={{ fontFamily: `'${bf}'`, fontSize: 16, lineHeight: 2, color: c.text_light, marginBottom: 36, maxWidth: 520, margin: '0 auto 36px' }}>
          {hero.subtitle}
        </p>
        <button className="zq-cta" style={{ backgroundColor: c.primary, color: '#fff', borderRadius: rad, padding: '16px 48px', fontSize: 16, fontFamily: `'${bf}'`, boxShadow: `0 10px 35px ${hexToRgba(c.primary, 0.4)}` }}>
          {hero.cta_text || 'اطلب الآن'}
        </button>
      </div>
    </section>
  );

  /* ════════ HERO: SPLIT (improved) ════════ */
  const renderHeroSplit = () => (
    <section className="zq-hero zq-grain" data-section="hero" style={{ display: 'grid', gridTemplateColumns: '52% 48%', minHeight: 560, position: 'relative', overflow: 'hidden', backgroundColor: c.bg }}>
      <div className="zq-float" style={{ position: 'absolute', top: 50, left: 70, width: 90, height: 90, borderRadius: '50%', border: `2px solid ${hexToRgba(c.primary, 0.1)}`, zIndex: 0 }} />

      <div dir="rtl" className="zq-hero-content" style={{ padding: '80px 60px', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
        {tagline && <div style={{ fontFamily: `'${bf}'`, fontSize: 12, color: c.primary, fontWeight: 700, letterSpacing: '0.15em', marginBottom: 20 }}>{tagline}</div>}
        <div className="zq-divider" style={{ marginBottom: 28 }} />
        <h1 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 46, lineHeight: 1.25, color: c.text, marginBottom: 24 }}>
          {hero.title || 'أهلاً بكم'}
        </h1>
        <p style={{ fontFamily: `'${bf}'`, fontSize: 16, lineHeight: 2.1, color: c.text_light, marginBottom: 40, maxWidth: 420 }}>
          {hero.subtitle}
        </p>
        <button className="zq-cta" style={{ backgroundColor: c.primary, color: '#fff', borderRadius: rad, padding: '16px 44px', fontSize: 16, fontFamily: `'${bf}'`, width: 'fit-content', boxShadow: `0 10px 35px ${hexToRgba(c.primary, 0.3)}` }}>
          {hero.cta_text || 'اطلب الآن'}
        </button>
      </div>

      <div className="zq-hero-img" style={{ position: 'relative', overflow: 'hidden' }}>
        <img src={hero.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80'} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to left, transparent 60%, ${c.bg})` }} />
        <div style={{ position: 'absolute', bottom: 30, left: 30, width: 80, height: 80, border: `3px solid ${hexToRgba(c.primary, 0.3)}`, borderRadius: rad }} />
      </div>
    </section>
  );

  const heroRenderers = { cinematic: renderHeroCinematic, editorial: renderHeroEditorial, minimal: renderHeroMinimal, immersive: renderHeroImmersive, split: renderHeroSplit };
  const renderHero = () => (heroRenderers[heroVariant] || renderHeroSplit)();

  /* ════════ STATS ════════ */
  const renderStats = () => {
    if (stats.length === 0) return null;
    return (
      <section data-section="stats" className={sc('stats')} dir="rtl" style={{ padding: '72px 48px', backgroundColor: c.bg_section }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div className="zq-stagger" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, 1fr)`, gap: 28 }}>
            {stats.map((s, i) => (
              <div key={i} className="zq-glass" style={{ textAlign: 'center', padding: '36px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 40, height: 3, background: c.primary, borderRadius: '0 0 2px 2px' }} />
                <div style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 40, color: c.primary, marginBottom: 10, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontFamily: `'${bf}'`, fontSize: 14, color: c.text_light, fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  /* ════════ FEATURES ════════ */
  const renderFeatures = () => {
    if (features.length === 0) return null;
    return (
      <section data-section="features" className={sc('features')} dir="rtl" style={{ padding: '100px 48px', backgroundColor: c.bg }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 20px' }} />
            <h3 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 36, color: c.text, lineHeight: 1.3 }}>لماذا نحن</h3>
          </div>
          <div className="zq-stagger" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(features.length, 3)}, 1fr)`, gap: 28 }}>
            {features.map((feat, i) => {
              const Icon = iconMap[feat.icon] || Star;
              return (
                <div key={i} className="zq-glass" style={{ textAlign: 'center', padding: '48px 32px' }}>
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: `linear-gradient(135deg, ${hexToRgba(c.primary, 0.12)}, ${hexToRgba(c.primary, 0.04)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', border: `1px solid ${hexToRgba(c.primary, 0.15)}` }}>
                    <Icon style={{ width: 28, height: 28, color: c.primary }} />
                  </div>
                  <h4 style={{ fontFamily: `'${hf}'`, fontWeight: 800, fontSize: 18, color: c.text, marginBottom: 12, lineHeight: 1.5 }}>{feat.title}</h4>
                  <p style={{ fontFamily: `'${bf}'`, fontSize: 14, lineHeight: 2, color: c.text_light }}>{feat.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  /* ════════ MENU ════════ */
  const renderMenu = () => {
    if (displayMenu.length === 0) return null;
    return (
      <section data-section="menu" className={sc('menu')} dir="rtl" style={{ padding: '100px 48px', backgroundColor: c.bg_section }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 20px' }} />
            <h3 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 36, color: c.text, lineHeight: 1.3, marginBottom: 10 }}>المنيو</h3>
            <p style={{ fontFamily: `'${bf}'`, fontSize: 15, color: c.text_light }}>أشهى الأطباق المحضّرة بعناية</p>
          </div>
          <div className="zq-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
            {displayMenu.map((item, i) => (
              <div key={i} className="zq-card">
                {item.image_url && (
                  <div className="zq-img-wrap" style={{ height: 190, position: 'relative' }}>
                    <img src={item.image_url} alt={item.name} />
                    <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${hexToRgba(c.card_bg, 0.5)}, transparent 50%)` }} />
                    {item.category && (
                      <span style={{ position: 'absolute', top: 14, right: 14, fontSize: 10, fontWeight: 700, padding: '4px 14px', borderRadius: 20, background: hexToRgba(c.primary, 0.9), color: '#fff', fontFamily: `'${bf}'` }}>{item.category}</span>
                    )}
                  </div>
                )}
                <div style={{ padding: item.image_url ? '20px 24px 24px' : '28px 24px' }}>
                  {!item.image_url && item.category && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20, background: hexToRgba(c.primary, 0.1), color: c.primary, fontFamily: `'${bf}'`, display: 'inline-block', marginBottom: 14 }}>{item.category}</span>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: item.description ? 12 : 0 }}>
                    <h4 style={{ fontFamily: `'${hf}'`, fontWeight: 800, fontSize: 17, color: c.text, lineHeight: 1.5, flex: 1 }}>{item.name}</h4>
                    <span className="zq-price" style={{ fontFamily: `'${hf}'`, fontSize: 14 }}>{item.price} ر.س</span>
                  </div>
                  {item.description && <p style={{ fontFamily: `'${bf}'`, fontSize: 13, lineHeight: 1.9, color: c.text_light }}>{item.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  /* ════════ ABOUT ════════ */
  const renderAbout = () => (
    <section data-section="about" className={sc('about')} dir="rtl" style={{ padding: '100px 48px', backgroundColor: c.bg }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: about.image_url ? '1fr 1fr' : '1fr', gap: 64, alignItems: 'center' }}>
        <div>
          <div className="zq-divider" style={{ marginBottom: 28 }} />
          <h3 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 36, lineHeight: 1.3, color: c.text, marginBottom: 24 }}>
            {about.title || 'عنّا'}
          </h3>
          <p style={{ fontFamily: `'${bf}'`, fontSize: 16, lineHeight: 2.2, color: c.text_light }}>
            {about.text || ''}
          </p>
          <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 40, height: 2, background: hexToRgba(c.primary, 0.3), borderRadius: 1 }} />
            <Quote style={{ width: 18, height: 18, color: hexToRgba(c.primary, 0.35) }} />
          </div>
        </div>
        {about.image_url && (
          <div style={{ position: 'relative' }}>
            <img src={about.image_url} alt="" style={{ width: '100%', height: 360, objectFit: 'cover', borderRadius: rad, boxShadow: `0 20px 60px ${hexToRgba(c.text, 0.12)}` }} />
            <div style={{ position: 'absolute', top: -12, right: -12, width: '100%', height: '100%', border: `2px solid ${hexToRgba(c.primary, 0.2)}`, borderRadius: rad, zIndex: -1 }} />
          </div>
        )}
      </div>
    </section>
  );

  /* ════════ CTA SECTION (NEW) ════════ */
  const renderCta = () => {
    const title = ctaData.title || 'جاهز تبدأ تجربتك؟';
    const subtitle = ctaData.subtitle || '';
    const btnText = ctaData.button_text || hero.cta_text || 'اطلب الآن';
    return (
      <section data-section="cta" className={sc('cta')} dir="rtl" style={{ padding: '80px 48px', background: `linear-gradient(135deg, ${c.primary}, ${c.secondary || c.primary})`, position: 'relative', overflow: 'hidden' }}>
        <div className="zq-float" style={{ position: 'absolute', top: -30, left: -30, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div className="zq-float-2" style={{ position: 'absolute', bottom: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <h3 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 36, color: '#FFFFFF', marginBottom: subtitle ? 16 : 32, lineHeight: 1.3 }}>{title}</h3>
          {subtitle && <p style={{ fontFamily: `'${bf}'`, fontSize: 16, color: 'rgba(255,255,255,0.8)', marginBottom: 32, lineHeight: 1.9 }}>{subtitle}</p>}
          <button className="zq-cta" style={{ backgroundColor: '#FFFFFF', color: c.primary, borderRadius: rad, padding: '16px 48px', fontSize: 16, fontFamily: `'${bf}'`, fontWeight: 800, boxShadow: '0 10px 35px rgba(0,0,0,0.15)' }}>
            {btnText}
          </button>
        </div>
      </section>
    );
  };

  /* ════════ TESTIMONIALS ════════ */
  const renderTestimonials = () => {
    if (testimonials.length === 0) return null;
    return (
      <section data-section="testimonials" className={sc('testimonials')} dir="rtl" style={{ padding: '100px 48px', backgroundColor: c.bg_section }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 20px' }} />
            <h3 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 36, color: c.text, lineHeight: 1.3 }}>آراء عملائنا</h3>
          </div>
          <div className="zq-stagger" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(testimonials.length, 3)}, 1fr)`, gap: 24 }}>
            {testimonials.map((t, i) => (
              <div key={i} className="zq-glass" style={{ padding: '36px 30px', position: 'relative' }}>
                <Quote style={{ width: 30, height: 30, color: hexToRgba(c.primary, 0.15), position: 'absolute', top: 18, left: 18 }} />
                <div className="zq-stars" style={{ marginBottom: 16 }}>
                  {Array.from({ length: t.rating || 5 }).map((_, j) => (
                    <Star key={j} style={{ width: 15, height: 15, fill: '#FBBF24' }} />
                  ))}
                </div>
                <p style={{ fontFamily: `'${bf}'`, fontSize: 14, lineHeight: 2.1, color: c.text_light, marginBottom: 20 }}>{t.text}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: `1px solid ${hexToRgba(c.text_light, 0.1)}`, paddingTop: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${c.primary}, ${c.secondary || c.primary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 800, fontFamily: `'${hf}'` }}>
                    {(t.name || '').charAt(0)}
                  </div>
                  <span style={{ fontFamily: `'${hf}'`, fontWeight: 700, fontSize: 14, color: c.text }}>{t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  /* ════════ GALLERY (Masonry-style) ════════ */
  const renderGallery = () => {
    if (gallery.length === 0) return null;
    const heights = [220, 280, 200, 260, 240, 210];
    return (
      <section data-section="gallery" className={sc('gallery')} dir="rtl" style={{ padding: '100px 48px', backgroundColor: c.bg }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 20px' }} />
            <h3 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 36, color: c.text, lineHeight: 1.3 }}>معرض الصور</h3>
          </div>
          <div className="zq-stagger" style={{ columns: gallery.length <= 2 ? 2 : 3, gap: 16 }}>
            {gallery.map((img, i) => (
              <div key={i} className="zq-card" style={{ breakInside: 'avoid', marginBottom: 16 }}>
                <div className="zq-img-wrap" style={{ height: heights[i % heights.length], borderRadius: rad }}>
                  <img src={img} alt="" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  /* ════════ CONTACT ════════ */
  const renderContact = () => {
    if (!contact.phone && !contact.address && !contact.hours) return null;
    return (
      <section data-section="contact" className={sc('contact')} dir="rtl" style={{ padding: '100px 48px', backgroundColor: c.bg_section }}>
        <div style={{ maxWidth: 960, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: 50, height: 3, background: c.primary, borderRadius: 2, margin: '0 auto 20px' }} />
          <h3 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 36, color: c.text, lineHeight: 1.3, marginBottom: 48 }}>تواصل معنا</h3>
          <div className="zq-stagger" style={{ display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
            {contact.phone && (
              <div className="zq-glass" style={{ padding: '32px 36px', textAlign: 'center', minWidth: 210 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${hexToRgba(c.primary, 0.12)}, ${hexToRgba(c.primary, 0.04)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: `1px solid ${hexToRgba(c.primary, 0.15)}` }}>
                  <PhoneIcon style={{ width: 22, height: 22, color: c.primary }} />
                </div>
                <span style={{ fontFamily: `'${bf}'`, fontSize: 15, color: c.text, fontWeight: 600, direction: 'ltr', display: 'block' }}>{contact.phone}</span>
              </div>
            )}
            {contact.address && (
              <div className="zq-glass" style={{ padding: '32px 36px', textAlign: 'center', minWidth: 210 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${hexToRgba(c.primary, 0.12)}, ${hexToRgba(c.primary, 0.04)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: `1px solid ${hexToRgba(c.primary, 0.15)}` }}>
                  <MapPin style={{ width: 22, height: 22, color: c.primary }} />
                </div>
                <span style={{ fontFamily: `'${bf}'`, fontSize: 15, color: c.text, fontWeight: 600 }}>{contact.address}</span>
              </div>
            )}
            {contact.hours && (
              <div className="zq-glass" style={{ padding: '32px 36px', textAlign: 'center', minWidth: 210 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${hexToRgba(c.primary, 0.12)}, ${hexToRgba(c.primary, 0.04)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: `1px solid ${hexToRgba(c.primary, 0.15)}` }}>
                  <Clock style={{ width: 22, height: 22, color: c.primary }} />
                </div>
                <span style={{ fontFamily: `'${bf}'`, fontSize: 15, color: c.text, fontWeight: 600 }}>{contact.hours}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  const sectionMap = { hero: renderHero, stats: renderStats, features: renderFeatures, menu: renderMenu, about: renderAbout, cta: renderCta, testimonials: renderTestimonials, gallery: renderGallery, contact: renderContact };

  /* ──── SOCIAL ICONS ──── */
  const SocialIcon = ({ type }) => {
    if (type === 'instagram') return <Instagram style={{ width: 18, height: 18 }} />;
    if (type === 'twitter') return <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
    if (type === 'snapchat') return <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.959-.289.054-.03.114-.05.178-.065a.544.544 0 01.548.27.664.664 0 01-.009.573c-.1.177-.35.35-.578.438a3.2 3.2 0 01-.437.135 1.07 1.07 0 00-.355.135c-.09.075-.15.18-.165.315-.015.12.009.21.024.27.036.12.12.285.24.525l.015.03c.48.885 1.11 1.59 1.89 2.1.36.24.72.39.99.465l.046.012c.105.03.195.06.27.09a.59.59 0 01.3.345.62.62 0 01-.045.465c-.255.435-.795.675-1.605.825-.24.045-.39.105-.45.18-.06.075-.075.15-.09.255l-.015.09c-.03.135-.075.255-.165.345a.634.634 0 01-.42.165c-.08 0-.16-.01-.24-.03a4.1 4.1 0 00-.87-.12c-.27 0-.5.03-.72.09-.38.1-.71.36-1.08.66-.56.45-1.2.975-2.16.975-.015 0-.03 0-.045 0-.015 0-.03 0-.045 0-.96 0-1.605-.525-2.16-.975-.37-.3-.705-.555-1.08-.66a2.59 2.59 0 00-.72-.09c-.33 0-.63.045-.87.12-.08.02-.16.03-.24.03a.634.634 0 01-.42-.165c-.09-.09-.135-.21-.165-.345l-.015-.09c-.015-.105-.03-.18-.09-.255-.06-.075-.21-.135-.45-.18-.81-.15-1.35-.39-1.605-.825a.62.62 0 01-.045-.465.59.59 0 01.3-.345c.075-.03.165-.06.27-.09l.046-.012c.27-.075.63-.225.99-.465.78-.51 1.41-1.215 1.89-2.1l.015-.03c.12-.24.204-.405.24-.525.015-.06.039-.15.024-.27-.015-.135-.075-.24-.165-.315a1.07 1.07 0 00-.355-.135 3.2 3.2 0 01-.437-.135c-.225-.09-.48-.26-.578-.438a.664.664 0 01-.009-.573.544.544 0 01.548-.27c.064.015.124.035.178.065.3.17.66.273.96.289.198 0 .326-.045.401-.09a8.5 8.5 0 01-.033-.57c-.104-1.628-.23-3.654.3-4.847C7.86 1.069 11.216.793 12.206.793z" /></svg>;
    if (type === 'tiktok') return <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.35 6.35 0 003.14 15.2a6.35 6.35 0 007.04 6.31 6.35 6.35 0 005.67-6.32V9.41a8.18 8.18 0 004.74 1.52V7.48a4.85 4.85 0 01-1-.79z" /></svg>;
    return null;
  };

  const activeSocials = Object.entries(social).filter(([, v]) => v);

  return (
    <div ref={rootRef} className={`zq-root overflow-y-auto ${className}`} style={{ backgroundColor: c.bg || '#fff', color: c.text || '#111', position: 'relative' }}>
      <style>{getCSS(c, f, rad, dark)}</style>

      {/* ──── NAVBAR ──── */}
      <nav dir="rtl" style={{ position: 'sticky', top: 0, zIndex: 40, backgroundColor: hexToRgba(c.nav_bg || c.bg, 0.9), backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: `1px solid ${hexToRgba(c.text_light, 0.08)}`, padding: '18px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 22, color: isDarkBg(c.nav_bg || c.bg) ? '#fff' : c.primary, letterSpacing: '-0.02em' }}>
          {rName}
        </h1>
        <div style={{ display: 'flex', gap: 32, fontSize: 13, fontWeight: 600 }}>
          {['المنيو', 'عنّا', 'تواصل'].map(lnk => (
            <span key={lnk} className="zq-nav-link" style={{ color: isDarkBg(c.nav_bg || c.bg) ? 'rgba(255,255,255,0.65)' : c.text_light }}>{lnk}</span>
          ))}
        </div>
      </nav>

      {/* ──── SECTIONS ──── */}
      {sectionOrder.map(key => {
        const renderFn = sectionMap[key];
        return renderFn ? <div key={key}>{renderFn()}</div> : null;
      })}

      {/* ──── FOOTER ──── */}
      <footer dir="rtl" style={{ padding: '56px 48px 36px', backgroundColor: isDarkBg(c.bg) ? hexToRgba(c.text, 0.05) : (c.text || '#111'), position: 'relative' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 28 }}>
          <div>
            <p style={{ fontFamily: `'${hf}'`, fontWeight: 900, fontSize: 22, color: c.primary, marginBottom: 8 }}>{rName}</p>
            {tagline && <p style={{ fontSize: 13, color: isDarkBg(c.bg) ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.5)', fontFamily: `'${bf}'` }}>{tagline}</p>}
          </div>
          {activeSocials.length > 0 && (
            <div style={{ display: 'flex', gap: 14 }}>
              {activeSocials.map(([type, url]) => (
                <a key={type} href={url} target="_blank" rel="noopener noreferrer" style={{ width: 40, height: 40, borderRadius: '50%', background: hexToRgba(c.primary, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.primary, transition: 'background 0.3s, transform 0.3s, color 0.3s', textDecoration: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.background = c.primary; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = hexToRgba(c.primary, 0.15); e.currentTarget.style.color = c.primary; e.currentTarget.style.transform = 'none'; }}>
                  <SocialIcon type={type} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div style={{ maxWidth: 960, margin: '28px auto 0', borderTop: `1px solid ${isDarkBg(c.bg) ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.08)'}`, paddingTop: 22, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: isDarkBg(c.bg) ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.35)', fontFamily: `'${bf}'` }}>
            {rName} &copy; {new Date().getFullYear()} &mdash; مدعوم بواسطة ذوق AI
          </p>
        </div>
      </footer>

      {showChat && <MiniChat branchId={config.branch_id} primaryColor={c.primary} />}
    </div>
  );
}
